import React, { useState, useEffect, useContext, useCallback } from 'react';
import { firestore } from '../../firebase/config';
import { BookingContext } from '../../context/BookingContext';
import './TimeSlots.css';

const TimeSlots = ({ practitionerId, selectedDate, serviceId, serviceDuration, onTimeSelect }) => {
  const { setCurrentStep, updateBookingData, bookingData } = useContext(BookingContext);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Récupérer les créneaux horaires disponibles
 const fetchAvailableTimeSlots = useCallback(async () => {
  if (!selectedDate || !practitionerId || !serviceId) return;

  setLoading(true);
  setError(null);
  setTimeSlots([]);

  try {
    const dateStr = selectedDate.toLocaleDateString('fr-CA');
    console.log("Recherche des créneaux pour:", dateStr);

    // Récupérer les données du service
    const serviceDoc = await firestore
      .collection('services')
      .doc(serviceId)
      .get();

    if (!serviceDoc.exists) {
      setError("Service non trouvé.");
      setLoading(false);
      return;
    }

    const serviceData = serviceDoc.data();
    const duration = serviceDuration || serviceData.duration || 60;
    const restTime = serviceData.restTime || 0;
    const dailyLimit = serviceData.dailyLimit || 0;

    // NOUVELLE VÉRIFICATION : Compatibilité du mode de consultation avec le service
    const consultationMode = bookingData.consultationMode;
    
    // Vérifier canBeInCabinet pour le mode cabinet
    if (consultationMode === 'cabinet' && serviceData.canBeInCabinet === false) {
      setError("Ce service ne peut pas être dispensé en cabinet.");
      setLoading(false);
      return;
    }
    
    // Vérifications existantes pour les autres modes
    if (consultationMode === 'remote' && !serviceData.canBeRemote) {
      setError("Ce service ne peut pas être dispensé à distance.");
      setLoading(false);
      return;
    }
    if (consultationMode === 'home' && !serviceData.canBeAtHome) {
      setError("Ce service ne peut pas être dispensé à domicile.");
      setLoading(false);
      return;
    }

    // Récupérer les disponibilités
    const availability = await getAvailabilityForDate(practitionerId, dateStr);

    if (!availability) {
      setError("Aucune disponibilité trouvée pour cette date.");
      setLoading(false);
      return;
    }

    // Vérifier si c'est un jour travaillé
    if (!availability.isWorkingDay) {
      setError("Le praticien ne travaille pas ce jour-là.");
      setLoading(false);
      return;
    }

    // Récupérer les rendez-vous existants
    const appointmentsSnapshot = await firestore
      .collection('appointments')
      .where('practitionerId', '==', practitionerId)
      .where('date', '==', dateStr)
      .where('status', '==', 'confirmed')
      .get();

    const bookedSlots = appointmentsSnapshot.docs.map(doc => {
      const data = doc.data();
      const appointmentDuration = data.duration || 60;
      const appointmentRestTime = data.restTime || 0;

      return {
        start: convertTimeToMinutes(data.time),
        end: convertTimeToMinutes(data.time) + appointmentDuration + appointmentRestTime,
        serviceId: data.serviceId,
        consultationMode: data.consultationMode || 'cabinet'
      };
    });

    // Vérifier la limite quotidienne
    if (dailyLimit > 0) {
      const serviceAppointmentsCount = bookedSlots.filter(slot => slot.serviceId === serviceId).length;
      if (serviceAppointmentsCount >= dailyLimit) {
        setError("La limite de rendez-vous quotidienne pour ce service est atteinte.");
        setLoading(false);
        return;
      }
    }

    // Récupérer les absences partielles
    const vacationsSnapshot = await firestore
      .collection('vacations')
      .where('practitionerId', '==', practitionerId)
      .where('isFullDay', '==', false)
      .get();

    const partialAbsences = [];
    vacationsSnapshot.docs.forEach(doc => {
      const vacation = doc.data();
      const startDate = new Date(vacation.startDate);
      const endDate = new Date(vacation.endDate);
      const selectedDateObj = new Date(dateStr);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      selectedDateObj.setHours(0, 0, 0, 0);

      if (selectedDateObj >= startDate && selectedDateObj <= endDate) {
        partialAbsences.push({
          start: convertTimeToMinutes(vacation.startTime),
          end: convertTimeToMinutes(vacation.endTime),
          reason: vacation.reason
        });
      }
    });

    const totalDuration = parseInt(duration) + parseInt(restTime);
    
    // LOGIQUE EXISTANTE CONSERVÉE : Adapter les données selon le mode d'input
    let slots = [];
    
    console.log("=== GÉNÉRATION DES CRÉNEAUX ===");
    console.log("Mode d'input:", availability.inputMode);
    console.log("Mode de consultation demandé:", consultationMode);
    
    if (availability.inputMode === 'full-day') {
      console.log("Mode journée complète - Paramètres:", {
        available: availability.fullDayAvailable,
        allowsCabinet: availability.fullDayAllowsCabinet,
        allowsRemote: availability.fullDayAllowsRemote,
        allowsHomeVisits: availability.fullDayAllowsHomeVisits
      });
      
      // Mode journée complète
      slots = generateTimeSlots({
        startTime: availability.fullDayStartTime,
        endTime: availability.fullDayEndTime,
        available: availability.fullDayAvailable,
        allowsCabinet: availability.fullDayAllowsCabinet !== false, // Par défaut true pour compatibilité
        allowsRemote: availability.fullDayAllowsRemote || false,
        allowsHomeVisits: availability.fullDayAllowsHomeVisits || false,
        hasLunchBreak: availability.fullDayHasLunchBreak,
        lunchBreakStart: availability.fullDayLunchBreakStart,
        lunchBreakEnd: availability.fullDayLunchBreakEnd,
        duration: duration,
        totalDuration: totalDuration,
        bookedSlots,
        partialAbsences,
        consultationMode: consultationMode,
        selectedDate
      });
    } else {
      console.log("Mode demi-journée - Paramètres matin:", {
        available: availability.morningAvailable,
        allowsCabinet: availability.morningAllowsCabinet,
        allowsRemote: availability.morningAllowsRemote,
        allowsHomeVisits: availability.morningAllowsHomeVisits,
        startTime: availability.morningStartTime,
        endTime: availability.morningEndTime
      });
      
      console.log("Mode demi-journée - Paramètres après-midi:", {
        available: availability.afternoonAvailable,
        allowsCabinet: availability.afternoonAllowsCabinet,
        allowsRemote: availability.afternoonAllowsRemote,
        allowsHomeVisits: availability.afternoonAllowsHomeVisits,
        startTime: availability.afternoonStartTime,
        endTime: availability.afternoonEndTime
      });
      
      // Mode demi-journée (par défaut ou explicite)
      const morningSlots = generateTimeSlots({
        startTime: availability.morningStartTime,
        endTime: availability.morningEndTime,
        available: availability.morningAvailable,
        allowsCabinet: availability.morningAllowsCabinet !== false, // Par défaut true pour compatibilité
        allowsRemote: availability.morningAllowsRemote || false,
        allowsHomeVisits: availability.morningAllowsHomeVisits || false,
        hasLunchBreak: availability.morningHasLunchBreak,
        lunchBreakStart: availability.morningLunchBreakStart,
        lunchBreakEnd: availability.morningLunchBreakEnd,
        duration: duration,
        totalDuration: totalDuration,
        bookedSlots,
        partialAbsences,
        consultationMode: consultationMode,
        selectedDate
      });

      const afternoonSlots = generateTimeSlots({
        startTime: availability.afternoonStartTime,
        endTime: availability.afternoonEndTime,
        available: availability.afternoonAvailable,
        allowsCabinet: availability.afternoonAllowsCabinet !== false, // Par défaut true pour compatibilité
        allowsRemote: availability.afternoonAllowsRemote || false,
        allowsHomeVisits: availability.afternoonAllowsHomeVisits || false,
        hasLunchBreak: availability.afternoonHasLunchBreak,
        lunchBreakStart: availability.afternoonLunchBreakStart,
        lunchBreakEnd: availability.afternoonLunchBreakEnd,
        duration: duration,
        totalDuration: totalDuration,
        bookedSlots,
        partialAbsences,
        consultationMode: consultationMode,
        selectedDate
      });

      slots = [...morningSlots, ...afternoonSlots];
    }

    console.log("Créneaux générés:", slots.length);
    setTimeSlots(slots);

    if (slots.length === 0) {
      setError("Aucun créneau disponible pour cette date.");
      if (window.updateDaysWithoutSlots) {
        window.updateDaysWithoutSlots(dateStr, true);
      }
    } else {
      if (window.updateDaysWithoutSlots) {
        window.updateDaysWithoutSlots(dateStr, false);
      }
    }

  } catch (error) {
    console.error("Erreur lors du chargement des créneaux horaires:", error);
    setError("Une erreur est survenue lors du chargement des créneaux horaires.");
  } finally {
    setLoading(false);
  }
}, [selectedDate, practitionerId, serviceId, serviceDuration, bookingData.consultationMode]);

// Fonction helper pour normaliser les données de disponibilité - AVEC SUPPORT CABINET EXPLICITE
const normalizeAvailabilityData = (availability) => {
  console.log("Données de disponibilité reçues:", availability);
  
  // Si c'est une ancienne disponibilité sans inputMode, la traiter comme half-day
  if (!availability.inputMode) {
    const normalized = {
      ...availability,
      inputMode: 'half-day',
      // Mapper les anciens champs vers les nouveaux champs demi-journée
      morningStartTime: availability.startTime || '09:00',
      morningEndTime: availability.lunchBreak?.start || '12:30',
      morningAvailable: availability.isWorkingDay,
      afternoonStartTime: availability.lunchBreak?.end || '13:30',
      afternoonEndTime: availability.endTime || '18:00',
      afternoonAvailable: availability.isWorkingDay,
      // Pour les anciennes données, Cabinet activé par défaut
      morningAllowsCabinet: true, // Cabinet toujours activé pour compatibilité legacy
      afternoonAllowsCabinet: true,
      // Remote et Home selon les anciennes données
      morningAllowsRemote: availability.allowsRemote || false,
      afternoonAllowsRemote: availability.allowsRemote || false,
      morningAllowsHomeVisits: availability.allowsHomeVisits || false,
      afternoonAllowsHomeVisits: availability.allowsHomeVisits || false,
    };
    console.log("Données normalisées (legacy):", normalized);
    return normalized;
  }
  
  console.log("Données modernes (avec inputMode):", availability);
  return availability;
};

  // Charger les créneaux disponibles lorsque la date change - INCHANGÉ
  useEffect(() => {
    if (selectedDate && practitionerId && serviceId) {
      console.log("Récupération des créneaux pour:", {
        date: selectedDate,
        dateFormatted: `${selectedDate.getFullYear()}-${selectedDate.getMonth() + 1}-${selectedDate.getDate()}`,
        practitionerId,
        serviceId,
        consultationMode: bookingData.consultationMode
      });
      fetchAvailableTimeSlots();
    } else {
      setTimeSlots([]);
      setSelectedTimeSlot(null);
    }
  }, [selectedDate, practitionerId, serviceId, serviceDuration, bookingData.consultationMode, fetchAvailableTimeSlots]);

  // Fonction pour récupérer les disponibilités - INCHANGÉE
const getAvailabilityForDate = async (practitionerId, dateStr) => {
  try {
    // Chercher une disponibilité spécifique pour cette date
    const specificSnapshot = await firestore
      .collection('availability')
      .where('practitionerId', '==', practitionerId)
      .where('type', '==', 'specific')
      .where('date', '==', dateStr)
      .limit(1)
      .get();

    if (!specificSnapshot.empty) {
      const specificAvailability = specificSnapshot.docs[0].data();
      console.log("Disponibilité spécifique trouvée pour", dateStr, ":", specificAvailability);
      return normalizeAvailabilityData(specificAvailability);
    }

    // Sinon, utiliser la disponibilité par défaut du jour de la semaine
    const selectedDateObj = new Date(dateStr);
    const dayOfWeek = selectedDateObj.getDay();
    const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    const defaultSnapshot = await firestore
      .collection('availability')
      .where('practitionerId', '==', practitionerId)
      .where('type', '==', 'default')
      .where('dayOfWeek', '==', adjustedDayOfWeek)
      .limit(1)
      .get();

    if (!defaultSnapshot.empty) {
      const defaultAvailability = defaultSnapshot.docs[0].data();
      console.log("Disponibilité par défaut trouvée pour le jour", adjustedDayOfWeek, ":", defaultAvailability);
      return normalizeAvailabilityData(defaultAvailability);
    }

    console.log("Aucune disponibilité trouvée pour", dateStr);
    return null;

  } catch (error) {
    console.error("Erreur lors de la récupération des disponibilités:", error);
    throw error;
  }
};

  // Générer les créneaux horaires en fonction des disponibilités - LOGIQUE EXPLICITE : SEULS LES MODES COCHÉS
  const generateTimeSlots = ({
    startTime,
    endTime,
    available,
    allowsCabinet,
    allowsRemote,
    allowsHomeVisits,
    hasLunchBreak,
    lunchBreakStart,
    lunchBreakEnd,
    duration,
    totalDuration,
    bookedSlots,
    partialAbsences = [],
    consultationMode = 'cabinet',
    selectedDate
  }) => {
    console.log(`Génération créneaux pour ${startTime}-${endTime}, mode: ${consultationMode}`, {
      available,
      allowsCabinet,
      allowsRemote,
      allowsHomeVisits,
      'allowsCabinet (boolean)': Boolean(allowsCabinet),
      'allowsRemote (boolean)': Boolean(allowsRemote),
      'allowsHomeVisits (boolean)': Boolean(allowsHomeVisits)
    });

    const slots = [];
    const interval = 15;

    if (!available) {
      console.log("Période non disponible, aucun créneau généré");
      return [];
    }

    // LOGIQUE EXPLICITE : Seuls les modes cochés sont disponibles
    if (consultationMode === 'cabinet') {
      if (!allowsCabinet) {
        console.log(`❌ Cabinet NON autorisé: allowsCabinet=${Boolean(allowsCabinet)}`);
        return [];
      }
      console.log("✅ Cabinet autorisé pour cette période");
    } else if (consultationMode === 'remote') {
      if (!allowsRemote) {
        console.log(`❌ Remote NON autorisé: allowsRemote=${Boolean(allowsRemote)}`);
        return [];
      }
      console.log("✅ Remote autorisé pour cette période");
    } else if (consultationMode === 'home') {
      if (!allowsHomeVisits) {
        console.log(`❌ Home NON autorisé: allowsHomeVisits=${Boolean(allowsHomeVisits)}`);
        return [];
      }
      console.log("✅ Home autorisé pour cette période");
    }

    const startMinutes = convertTimeToMinutes(startTime);
    const endMinutes = convertTimeToMinutes(endTime);
    const lunchStartMinutes = hasLunchBreak && lunchBreakStart ? convertTimeToMinutes(lunchBreakStart) : null;
    const lunchEndMinutes = hasLunchBreak && lunchBreakEnd ? convertTimeToMinutes(lunchBreakEnd) : null;

    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isToday = selectedDate.getFullYear() === today.getFullYear() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getDate() === today.getDate();

    const currentMinutes = isToday
      ? now.getHours() * 60 + now.getMinutes() + 60
      : 0;

    // BOUCLE DE GÉNÉRATION DES CRÉNEAUX - LOGIQUE EXISTANTE INCHANGÉE
    for (let minutes = startMinutes; minutes <= endMinutes - duration; minutes += interval) {
      if (isToday && minutes < currentMinutes) continue;

      const slotEndMinutes = minutes + duration;
      const totalSlotEndMinutes = minutes + totalDuration;

      const isDuringLunch = hasLunchBreak && lunchStartMinutes !== null &&
        ((minutes < lunchEndMinutes && slotEndMinutes > lunchStartMinutes));

      const isDuringAbsence = partialAbsences.some(abs => minutes < abs.end && slotEndMinutes > abs.start);

      const isOverlapping = bookedSlots.some(slot => minutes < slot.end && totalSlotEndMinutes > slot.start);

      if (!isDuringLunch && !isDuringAbsence && !isOverlapping) {
        slots.push({
          time: minutesToTimeString(minutes),
          minutes,
          endTime: minutesToTimeString(minutes + duration),
          endTimeWithRest: minutesToTimeString(minutes + totalDuration),
          consultationMode
        });
      }
    }

    console.log(`${slots.length} créneaux générés pour ${startTime}-${endTime} en mode ${consultationMode}`);
    return slots;
  };

  // Fonctions utilitaires existantes - INCHANGÉES
  const convertTimeToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTimeString = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Gérer la sélection d'un créneau horaire - INCHANGÉ
  const handleTimeSlotSelect = (timeSlot) => {
    setSelectedTimeSlot(timeSlot.time);

    // Notifier le composant parent
    if (onTimeSelect) {
      onTimeSelect(timeSlot.time);
    }

    // Mettre à jour la date formatée pour l'affichage
    const formattedDate = selectedDate.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    updateBookingData({
      formattedDate,
      consultationMode: bookingData.consultationMode
    });

    // Passer à l'étape suivante automatiquement
    setCurrentStep(5);
  };

  // Gérer le retour à l'étape précédente - INCHANGÉ
  const handleBack = () => {
    setCurrentStep(3);
  };

  // RENDU INCHANGÉ
  return (
    <div className="time-slots-container">
      <h2>Choisissez un horaire</h2>
      <p className="date-selected">
        {selectedDate.toLocaleDateString('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        })}
      </p>

      {/* Affichage du mode de consultation */}
      {bookingData.consultationMode && (
        <div className="consultation-mode-reminder">
          <span className={`mode-badge ${bookingData.consultationMode}`}>
            {bookingData.consultationMode === 'cabinet' && '🏢 Consultation en cabinet'}
            {bookingData.consultationMode === 'remote' && '💻 Consultation à distance'}
            {bookingData.consultationMode === 'home' && '🏠 Consultation à domicile'}
          </span>
        </div>
      )}

      {loading && (
        <div className="time-slots-loading">
          <div className="loading-spinner"></div>
          <p>Chargement des horaires...</p>
        </div>
      )}

      {error && (
        <div className="time-slots-error">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && timeSlots.length === 0 && (
        <div className="no-time-slots">
          <p>Aucun horaire disponible pour cette date.</p>
          <p className="suggestion">Essayez de sélectionner une autre date ou contactez le praticien.</p>
        </div>
      )}

      {!loading && !error && timeSlots.length > 0 && (
        <div className="time-slots-grid">
          {timeSlots.map((slot, index) => (
            <div
              key={index}
              className={`time-slot ${selectedTimeSlot === slot.time ? 'selected' : ''}`}
              onClick={() => handleTimeSlotSelect(slot)}
              title={`${slot.time} - ${slot.endTime}`}
            >
              {slot.time}
            </div>
          ))}
        </div>
      )}

      <div className="step-navigation">
        <button className="btn btn-back" onClick={handleBack}>
          Retour
        </button>
      </div>
    </div>
  );
};

export default TimeSlots;